import { useRef, useState } from 'react';
import { Alert, Button, ProgressBar, Spinner } from 'react-bootstrap';
import { uploadApi } from '../../api';
import { fileUrl } from '../../utils/format';

const ICONS = { pdf: 'bi-file-earmark-pdf', image: 'bi-file-earmark-image' };

/**
 * Attach sonography, blood reports and x-rays to a patient. Files are uploaded
 * as soon as they are chosen and the resulting links are handed back through
 * onChange, so the same component works on the registration form (no patient
 * id yet) and on the patient record.
 */
export default function AttachmentField({ value = [], onChange, disabled, compact }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);

  async function send(fileList) {
    if (!fileList?.length) return;

    setBusy(true);
    setError('');
    setProgress(0);
    try {
      const res = await uploadApi.files(fileList, setProgress);
      onChange([...value, ...res.data]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove(index) {
    const report = value[index];
    onChange(value.filter((_, i) => i !== index));
    // Only unsaved uploads need cleaning up here; saved ones go through the
    // patient endpoint, which deletes the file itself.
    if (!report._id) uploadApi.discard(report).catch(() => {});
  }

  function setNote(index, note) {
    onChange(value.map((r, i) => (i === index ? { ...r, note } : r)));
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
        onChange={(e) => send(e.target.files)}
      />

      <div
        className={`attach-drop ${dragging ? 'is-dragging' : ''} ${disabled || busy ? 'is-disabled' : ''}`}
        onClick={() => !disabled && !busy && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled && !busy) send(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        {busy ? (
          <>
            <Spinner animation="border" size="sm" className="mb-2" />
            <div className="small">Uploading...</div>
            <ProgressBar now={progress} className="mt-2" style={{ height: 6 }} />
          </>
        ) : (
          <>
            <i className="bi bi-cloud-arrow-up" />
            <div className="fw-semibold small">
              {compact ? 'Attach a report' : 'Click to choose files, or drop them here'}
            </div>
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              Sonography, blood reports, x-rays &mdash; JPG, PNG or PDF, up to 10 MB each
            </div>
          </>
        )}
      </div>

      {error && (
        <Alert variant="danger" className="py-2 small mt-2 mb-0">
          <i className="bi bi-exclamation-triangle me-1" />
          {error}
        </Alert>
      )}

      {value.length > 0 && (
        <div className="attach-list mt-2">
          {value.map((r, i) => (
            <div className="attach-item" key={r._id || r.url || i}>
              <i className={`bi ${ICONS[r.type] || 'bi-file-earmark'} attach-icon`} />

              <div className="flex-grow-1 min-w-0">
                <a
                  href={fileUrl(r.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="attach-name"
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.name}
                </a>
                <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                  {r.sizeKb ? `${(r.sizeKb / 1024).toFixed(1)} MB` : ''}
                </div>
                {!disabled && (
                  <input
                    className="attach-note"
                    value={r.note || ''}
                    placeholder="Add a note, e.g. USG Abdomen 12 Aug"
                    onChange={(e) => setNote(i, e.target.value)}
                  />
                )}
                {disabled && r.note ? <div className="small text-muted">{r.note}</div> : null}
              </div>

              {!disabled && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="btn-icon flex-shrink-0"
                  onClick={() => remove(i)}
                  title={`Remove ${r.name}`}
                  aria-label={`Remove ${r.name}`}
                >
                  <i className="bi bi-trash" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
